import { BaseReactComponent } from "@/Common/Components/BaseReactComponent";
import { SpanControl } from "@/Common/Components/SpanControl";
import { CommonTableHeaderComponent } from "@/Common/Components/Tables/CommonTableHeaderComponent";
import { MyleTable } from "@/Common/Components/Tables/MyleTable";
import { MyleTableColumn, MyleTableColumnOptions } from "@/Common/Components/Tables/MyleTableColumn";
import { MyleTableColumnOrderControlv2 } from "@/Common/Components/Tables/MyleTableColumnOrderControlv2";
import { SimplePagerControl } from "@/Common/Components/Tables/SimplePagerControl";
import { MyleHelper } from "@/Common/Helpers/MyleHelper";
import { Person } from "@/Common/Models/Person/Person";
import { PortfolioExamRequestSortOrderType, PortfolioInboxes, RedirectTo, SortDirection } from "@/Common/Models/UI/Enums";
import { ColumnConfigurationModel } from "@/Common/Models/UI/Tables/ColumnConfigurationModel";
import { ITableRenderOptions } from "@/Common/Models/UI/Tables/ITableRenderOptions";
import { MyleTableTarget } from "@/Common/Models/UI/Tables/MyleTableTarget";
import * as LocalOperations from "@/Common/Operations/operations.local";
import * as ServerOperations from "@/Common/Operations/operations.server";
import { RouterHistory } from "@/Common/Utilities/Router/RouterHistory";
import { ExportableFile } from "@/ExportableFile/Models/ExportableFile";
import { ApssClient } from "@/Patient/Api/Apss/ApssClient";
import { MyAPSSFormTableItem } from "@/Portfolio/Models/MyNotesInboxTableItem";
import { IAPSSInboxModel } from "@/Portfolio/Models/UI/IAPSSInboxModel";
import { ApssInbox } from "./APSSInbox";
import { ConseilNumeriqueSettingsContainer } from "./ConseilNumeriqueInboxSettingsContainer";
import { PortfolioSaveOptionKey } from "./PortfolioSaveOptionKey";

interface IConseilNumeriqueInboxProps {
    Model: IAPSSInboxModel;
}

class ConseilNumeriqueInbox extends BaseReactComponent<IConseilNumeriqueInboxProps, any> {
    private readonly helper: MyleHelper = sessionStore.helper;

    public componentDidMount(): void {
        $(window).trigger("resize");

        sessionStore.dispatch(LocalOperations.Portfolio.APSS_ConseilNumerique_Reload);
    }

    public render(): JSX.Element {
        const saveOptionKey = PortfolioSaveOptionKey.ApssConseilNumerique;
        const columnsOptions = this.helper.loadTableColumnOptionsFromSettings(saveOptionKey, this.getMyleTableColumnsDefaultOptions());
        const tableColumns = this.getTableColumns(columnsOptions);
        const tableRenderOptions = this.loadTableRenderOptions(columnsOptions);
        const cfgm: ColumnConfigurationModel[] = this.helper.castColumnConfigurationToMyleTableColumnOptions(columnsOptions.toArray());

        return (
            <div className="workspace">
                <CommonTableHeaderComponent
                    title={cacheStore.GetString("ConseilNumeriqueActionRequiredLabel")}
                    searchText={this.props.Model.get("GlobalPortfolioSearchText")}
                    searchPlaceholder={cacheStore.GetString("SearchField")}
                    onSearch={text => sessionStore.dispatch(LocalOperations.Portfolio.APSS_ConseilNumerique_SearchChange, { value: text })}
                    onRefreshClick={() => sessionStore.dispatch(LocalOperations.Portfolio.APSS_ConseilNumerique_Reload)}
                    onSettingsClick={() => sessionStore.dispatch(LocalOperations.Portfolio.APSS_ToggleOpenCloseSettings)}
                />
                <div id="posref" className="ws-scrollable">
                    <div className="myle-doc-tb main-content table-wrapper">
                        {this.props.Model.get("ShowSettings")
                            && (
                                <ConseilNumeriqueSettingsContainer
                                    className="filter-component"
                                    model={this.props.Model}
                                    Type={PortfolioInboxes.APSS_ConseilNumerique}
                                    style={{ width: "650px" }}
                                    onClick={(e: React.MouseEvent) => { e.stopPropagation(); }}
                                    onCloseCallback={() => sessionStore.dispatch(LocalOperations.Portfolio.APSS_ConseilNumerique_Reload)}
                                    reloadOpetation={LocalOperations.Portfolio.APSS_ConseilNumerique_Reload}
                                />
                            )
                        }
                        <MyleTable
                            TableRenderOptions={tableRenderOptions}
                            TableId="RequestsInbox_main_tb"
                            Columns={tableColumns}
                            DataSource={this.props.Model.get("ConseilNumeriqueTableData")}
                            ColumnsPreferencesChangedOperation={ServerOperations.Server.Myle_UpdateUserSettings}
                            RowClickOperation={LocalOperations.Portfolio.APSS_RowClick}
                            ColumnSortTarget={MyleTableTarget.Unassigned}
                            SaveOptionskeyTarget={saveOptionKey}
                        />
                    </div>
                </div>
                <div className="ws-footer table-pager-footer">
                    <div className="row">
                        <div className="col-lg-10 col-md-9 col-sm-8">
                            <SimplePagerControl
                                PageSize={this.props.Model.get("ConseilNumeriqueSelectedPageSizeOption")}
                                TotalRows={this.props.Model.get("ConseilNumeriqueTotalQueryRows")}
                                CurrentPage={this.props.Model.get("ConseilNumeriqueCurrentPage")}
                                EventToDispatchOnPageChange={LocalOperations.Portfolio.APSS_ConseilNumerique_PageEvent}
                            />
                        </div>
                    </div>
                </div>

                {this.props.Model.get("ConseilNumeriqueColumnOrderingPopupOpen") && (
                    <MyleTableColumnOrderControlv2
                        TableColumns={cfgm}
                        SaveOptionskeyTarget={saveOptionKey}
                        ContainerId="columnSettingsPopup"
                        CloseColumOrder={LocalOperations.Portfolio.APSS_ConseilNumerique_HideColumnOrdering}
                    />
                )}
            </div>
        );
    }

    private getMyleTableColumnsDefaultOptions(): MyleTableColumnOptions[] {
        return [
            new MyleTableColumnOptions({
                HeaderText: cacheStore.GetString("PatientLabel"),
                FieldName: "APSSPatient", // nom + prenom + DDN
                WidthPercentageDefault: 10,
                RowClickEnabled: false,
                IsSortable: false,
                SortColumn: PortfolioExamRequestSortOrderType.Patient,
                SortDirection: SortDirection.ASC
            }),
            new MyleTableColumnOptions({
                HeaderText: cacheStore.GetString("Id"),
                FieldName: "ReqReg_Id",
                WidthPercentageDefault: 8,
                IsHidden: true,
                RowClickEnabled: true
            }),
            new MyleTableColumnOptions({
                HeaderText: cacheStore.GetString("CreatedOnDLabel"),
                FieldName: "ReqReg_DateCreation",
                WidthPercentageDefault: 8,
                RowClickEnabled: true,
                IsSortable: false
            }),
            new MyleTableColumnOptions({
                HeaderText: cacheStore.GetString("DateSentLabel"),
                FieldName: "dateTransmissionCRDS",
                WidthPercentageDefault: 8,
                RowClickEnabled: true
            }),
            new MyleTableColumnOptions({
                HeaderText: sessionStore.getDefaultHealthCardLabel(),
                FieldName: "NAM",
                WidthPercentageDefault: 8,
                IsHidden: true,
                RowClickEnabled: true
            }),
            new MyleTableColumnOptions({
                HeaderText: cacheStore.GetString("CreatedByLabel"),
                FieldName: "ReqReg_NomCreateur",
                IsHidden: true,
                WidthPercentageDefault: 8,
                RowClickEnabled: true
            }),
            new MyleTableColumnOptions({
                HeaderText: cacheStore.GetString("MDReferantLabel"),
                FieldName: "nomRef", // + prenomRef
                WidthPercentageDefault: 8,
                RowClickEnabled: true
            }),
            new MyleTableColumnOptions({
                HeaderText: cacheStore.GetString("StatusLabel"),
                FieldName: "etat_texte",
                WidthPercentageDefault: 12,
                RowClickEnabled: true
            }),
            new MyleTableColumnOptions({
                HeaderText: cacheStore.GetString("SubStateLabel"),
                FieldName: "sousEtat_texte",
                WidthPercentageDefault: 12,
                RowClickEnabled: true
            }),
            new MyleTableColumnOptions({
                HeaderText: cacheStore.GetString("LastUpdateLabel"),
                FieldName: "ReqReg_DateChangementEtat",
                WidthPercentageDefault: 8,
                RowClickEnabled: true
            }),
            new MyleTableColumnOptions({
                HeaderText: cacheStore.GetString("NotesLabel"),
                FieldName: "Note",
                WidthPercentageDefault: 14,
                RowClickEnabled: true
            }),
            new MyleTableColumnOptions({
                HeaderText: cacheStore.GetString("AttachmentsLabel"),
                FieldName: "lstPrerequis",
                WidthPercentageDefault: 12,
                RowClickEnabled: false
            }),
            new MyleTableColumnOptions({
                HeaderText: cacheStore.GetString("ActionLabel"),
                FieldName: "Actions",
                WidthPercentageDefault: 12,
                RowClickEnabled: false
            })
        ];
    }

    private getTableColumns(columnsOptions: Immutable.List<MyleTableColumnOptions>): Immutable.List<MyleTableColumn> {
        return MyleTableColumn.buildList<MyAPSSFormTableItem>(
            columnsOptions,
            column => {
                switch (column.FieldName) {
                    case "APSSPatient":
                        return {
                            renderCell: item => {
                                if (item.PatientLink != null)
                                    return (<div><a onClick={() => this.openPatient(item.PatientLink.P, item.PatientLink.E)}>{item.Model.prenom + " " + item.Model.nom}</a></div>);
                                else
                                    return (<div><SpanControl IsBold={item.IsBold()} text={item.Model.prenom + " " + item.Model.nom} /></div>);
                            }
                        };

                    case "ReqReg_Id":
                        return {
                            renderCell: item => (
                                <div><SpanControl IsBold={item.IsBold()} text={item.Model.ReqReg_Id} /></div>
                            )
                        };

                    case "NAM":
                        return {
                            renderCell: item => (
                                <div><SpanControl IsBold={item.IsBold()} text={item.Model.NAM} /></div>
                            )
                        };

                    case "ReqReg_DateCreation":
                        return {
                            renderCell: item => {
                                let dateString = "";
                                if (item.Model.ReqReg_DateModification != null) {
                                    const tmpDate: Date = new Date(item.Model.ReqReg_DateCreation);
                                    dateString = tmpDate.toMoment().format("YYYY-MM-DD HH:mm");
                                }
                                return (
                                    <div><SpanControl IsBold={item.IsBold()} text={dateString} /></div>
                                );
                            }
                        };

                    case "dateTransmissionCRDS":
                        return {
                            renderCell: item => {
                                let dateString = "";
                                if (item.Model.ReqReg_DateModification != null) {
                                    const tmpDate: Date = new Date(item.Model.ReqReg_DateModification);
                                    dateString = tmpDate.toMoment().format("YYYY-MM-DD HH:mm");
                                }

                                return (
                                    <div><SpanControl IsBold={item.IsBold()} text={dateString} /></div>
                                );
                            }
                        };

                    case "etat_texte":
                        return {
                            renderCell: item => (
                                <div>
                                    <SpanControl
                                        IsBold={item.IsBold()}
                                        text={`${item.Model.etat_texte}`}
                                        title={`${item.Model.etat_texte}`}
                                    />
                                </div>
                            )
                        };

                    case "sousEtat_texte":
                        return {
                            renderCell: item => (
                                <div>
                                    <SpanControl
                                        IsBold={item.IsBold()}
                                        text={`${item.Model.sousEtat_texte}`}
                                        title={`${item.Model.sousEtat_texte}`}
                                    />
                                </div>
                            )
                        };

                    case "ReqReg_NomCreateur":
                        return {
                            renderCell: item => (
                                <div><SpanControl IsBold={item.IsBold()} text={item.Model.ReqReg_NomCreateur} /></div>
                            )
                        };

                    case "nomRef":
                        return {
                            renderCell: item => (
                                <div><SpanControl IsBold={item.IsBold()} text={item.Model.prenomRef + " " + item.Model.nomRef} /></div>
                            )
                        };

                    case "ReqReg_DateChangementEtat":
                        return {
                            renderCell: item => {
                                let dateString = item.Model.ReqReg_DateChangementEtat
                                    ? new Date(item.Model.ReqReg_DateChangementEtat).toMoment().format("YYYY-MM-DD HH:mm")
                                    : "";

                                return (
                                    <div><SpanControl IsBold={item.IsBold()} text={dateString} /></div>
                                );
                            }
                        };

                    case "Note":
                        return {
                            renderCell: item => (
                                <div><SpanControl
                                    IsBold={item.IsBold()}
                                    text={item.Model.changementEtat_Detail ?? ""}
                                    title={item.Model.changementEtat_Detail ?? ""}
                                />
                                </div>
                            )
                        };

                    case "Actions":
                        return {
                            renderCell: item => {
                                const isArchived = item.Model.dmeReserve_archiveFlag == "1";
                                return (
                                    <div>
                                        {!isArchived &&
                                            (
                                                <div data-cy="conseil-numerique-archive-anchor">
                                                    <a
                                                        key={"action_archive_" + item.Model.ReqReg_Id}
                                                        onClick={async () => this.archiveConseilNumerique(item.Model.ReqReg_Id, item.Model.ReqReg_EtablissementCreateur)}
                                                    >
                                                        {cacheStore.GetString("ArchiveLabel")}
                                                    </a>
                                                </div>
                                            )
                                        }
                                        {isArchived &&
                                            (
                                                <div>
                                                    <a
                                                        key={"action_unarchive_" + item.Model.ReqReg_Id}
                                                        onClick={async () => this.restoreConseilNumerique(item.Model.ReqReg_Id, item.Model.ReqReg_EtablissementCreateur)}
                                                    >
                                                        {cacheStore.GetString("RestoreLabel")}
                                                    </a>
                                                </div>
                                            )
                                        }
                                    </div>
                                );
                            }
                        };

                    case "lstPrerequis":
                        return {
                            renderCell: item => {
                                if (item.ExpandPrerequis || item.Prerequis.length == 0) {
                                    return (
                                        <div>
                                            {item.Prerequis.map((x, idx) => {
                                                if (x.IsMissing) {
                                                    return (
                                                        <div key={item.Model.ReqReg_Id + "_prer_" + idx} className="flex-container" style={{ justifyContent: "flex-start" }}>
                                                            <div><a onClick={() => this.openAttachPopup(item.Model.ReqReg_Id, x.Code, item)}>{cacheStore.GetString("AttachLabel")}</a></div>
                                                            <div style={{ marginLeft: "8px" }}><SpanControl IsRed={true} text={x.Name} /></div>
                                                        </div>
                                                    );
                                                }
                                                else
                                                    return <div key={item.Model.ReqReg_Id + "_prer_" + idx} onClick={e => ApssInbox.openAPSSDocument(item.Model.ReqReg_Id, x.Code, e)}><a style={{ color: "#1e355c" }}><SpanControl text={x.Name} /></a></div>;
                                            })}

                                            <div key={item.Model.ReqReg_Id + "_prer_" + "autre"} className="flex-container" style={{ justifyContent: "flex-start" }}>
                                                <div><a onClick={() => this.openAttachPopup(item.Model.ReqReg_Id, "demandeurDocument", item)}>{cacheStore.GetString("AttachLabel")}</a></div>
                                                <div style={{ marginLeft: "8px" }}>
                                                    <div onClick={e => ApssInbox.openAPSSDocument(item.Model.ReqReg_Id, "demandeurDocument", e)}>
                                                        <a style={{ color: "#1e355c" }}><SpanControl IsBold={item.IsBold()} text={cacheStore.GetString("OtherReportLabel")} /></a>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                }
                                else {
                                    const documentsDisplay = item.Prerequis.length > 0
                                        ? "(" + (item.Prerequis.length - item.PrerequisManquantExistingCount) + "/" + item.Prerequis.length + ")"
                                        : "";

                                    const isRed = item.PrerequisManquantExistingCount > 0;

                                    return (<div><a style={{ color: "#1e355c" }} onClick={() => this.expandAPSSTableItem(item.Model.ReqReg_Id)}><SpanControl IsBold={item.IsBold()} IsRed={isRed} text={documentsDisplay} /></a></div>);
                                }
                            }
                        };

                    default:
                        return {
                            renderCell: () => (
                                <div>TODO</div>
                            )
                        };
                }
            },
            false
        );
    }

    private loadTableRenderOptions(columnOptions: Immutable.List<MyleTableColumnOptions>): ITableRenderOptions {
        // Get The Render Options
        return Immutable.fromJS({
            MinColWidth: 60,
            MinTableWidth: 980,
            Cols: columnOptions.filter(x => x.IsHidden === false).map(i => i.WidthPercentage),
            FirstColumnNoDrag: false,
            LastColumnNoDrag: true,
            Autoresize: true,
            OnColumnWidthChange: null
        });
    }

    private openAttachPopup(requestId: string, documentCode: string, item: MyAPSSFormTableItem): void {
        const requestData = {
            AttachmentPopupResponseCallback: (attachments: ExportableFile[]) => portfolioStore.PortfolioActions.AttachAPSSFiles(requestId, documentCode, attachments),
            AttachmentPopupMaxFiles: 5,
            Model: this.props.Model
        };

        if (item.PatientLink == null)
            requestData["AttachmentPopupPatientSearch"] = item.Model.nom + " " + item.Model.prenom + " " + new Date(item.Model.DDN).getFullYear().toString();
        else
            requestData["AttachmentPopupPatientPerson"] = new Person({
                ContactId: item.PatientLink.P,
                FirstName: item.Model.prenom,
                LastName: item.Model.nom,
                NAM: item.Model.NAM
            });

        sessionStore.dispatchMain(LocalOperations.MainComponentViewOperations.DisplayAttachmentPopup, requestData);
    }

    private expandAPSSTableItem(id: string): void {
        const data = portfolioStore.apssInboxModel.get("TableData").toArray();
        if (data !== null) {
            for (const item of data) {
                item.ExpandPrerequis = false;

                if (item.Model.ReqReg_Id == id)
                    item.ExpandPrerequis = true;
            }
        }

        sessionStore.dispatch(
            LocalOperations.Portfolio.APSS_ExpandTableItem,
            {
                "id": id,
                "data": data.toImmutable()
            }
        );
    }

    private openPatient(patientContactId: string, NoteId: string): void {
        window.setTimeout(() => {
            sessionStore.dispatch(
                LocalOperations.Misc.RedirectToTab,
                {
                    "value": patientContactId,
                    "target": RedirectTo.Patient,
                    "currentLocation": RouterHistory.location,
                    "OpenEncounterId": NoteId,
                    "encounterEdit": false
                }
            );
        }, 100);
    }

    private async archiveConseilNumerique(apssFormId: string, historiqueId: string): Promise<void> {
        await ApssClient.ArchiveRequest(apssFormId, historiqueId);
        sessionStore.dispatch(LocalOperations.Portfolio.APSS_ConseilNumerique_Reload);
    }

    private async restoreConseilNumerique(apssFormId: string, historiqueId: string): Promise<void> {
        await ApssClient.RestoreRequest(apssFormId, historiqueId);
        sessionStore.dispatch(LocalOperations.Portfolio.APSS_ConseilNumerique_Reload);
    }
}

export { ConseilNumeriqueInbox };
